import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import servicesConfig from '../config/servicesConfig.json';
import SEO from '../components/SEO';

const Services = () => {
  const { categories } = servicesConfig;

  const getCategorySummary = (category) => {
    if (category.intro) {
      return category.intro.split('\n')[0];
    }
    if (category.services && category.services.length > 0) {
      return category.services[0].description.split('\n')[0];
    }
    if (category.subcategories && category.subcategories.length > 0) {
      return category.subcategories[0].services[0].description.split('\n')[0];
    }
    return 'Explore this treatment category.';
  };

  const seoSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Skincare & Aesthetic Treatments in Widnes',
    description:
      'Explore skincare and aesthetic treatment categories including acne support, facials, skin boosters, and consultations in Widnes.',
    url: 'https://artaestheticsskincare.com/services',
    mainEntity: categories.map((category) => ({
      '@type': 'Service',
      name: category.title,
      url: `https://artaestheticsskincare.com/services/${category.id}`,
      areaServed: ['Widnes', 'Runcorn', 'Warrington', 'St Helens', 'Liverpool']
    }))
  };

  return (
    <section className="services">
      <SEO
        title="Skincare & Aesthetic Treatments in Widnes | ART Aesthetics"
        description="Explore acne-focused skincare, consultations, facials, skin boosters, anti-wrinkle and functional testing treatments in Widnes and nearby towns."
        path="/services"
        jsonLd={seoSchema}
      />

      <div className="services-container">
        <h1 className="page-title">Treatments for Skin Confidence</h1>
        <p className="page-subtitle">
          Explore treatment categories built around skin health, acne support, and natural-looking results in Widnes and nearby towns.
        </p>

        <div className="services-hub-grid">
          {categories.map((category, idx) => (
            <motion.article
              key={category.id}
              className="service-hub-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: idx * 0.06 }}
              viewport={{ once: true }}
            >
              <div className="service-hub-card-header">
                <span className="category-icon" aria-hidden="true">{category.icon}</span>
                <h2>{category.title}</h2>
              </div>

              <p className="service-hub-summary">{getCategorySummary(category)}</p>

              <ul className="service-hub-highlights">
                {(category.services || category.subcategories?.flatMap((group) => group.services) || [])
                  .slice(0, 3)
                  .map((service) => (
                    <li key={service.title}>{service.title}</li>
                  ))}
              </ul>

              <Link className="service-hub-link" to={`/services/${category.id}`}>
                Explore {category.title} →
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          className="service-pathways"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Not Sure Where to Start?</h2>
          <p>Most acne and skin-confidence journeys begin with a consultation and a focused treatment plan.</p>
          <div className="service-pathways-links">
            <Link to="/services/consultations">Start with a Consultation</Link>
            <Link to="/services/facials">Explore Acne-Supportive Facials</Link>
            <Link to="/services/functional-testing">Look Deeper with Functional Testing</Link>
          </div>
        </motion.div>
        
        {/* CTA Section */}
        <motion.div
          className="services-cta"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Ready to Book Your Treatment?</h2>
          <p>Start your journey to radiant skin today</p>
          <a 
            href="https://that-time.co.uk/art-aesthetics-skincare" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="cta-button primary"
          >
            Book Now
          </a>
        </motion.div>

        {/* Policy Note */}
        <motion.div
          className="policy-note"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p>
            📌 <strong>Please Note:</strong> A deposit is required when booking. 
            48 hours notice required for cancellations or reschedules. 
            See our <Link to="/booking-policy">Booking Policy</Link> for full cancellation terms.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
